import { cn } from "../cn";

/** Line icons from the approved mockup (24×24, stroke=currentColor). Inlined as
    a name→markup map so consumers get a single `<Icon name=… />` with no external
    sprite file. */
const ICONS = {
  grid: '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>',
  case: '<rect x="2.5" y="7" width="19" height="13" rx="2.5"/><path d="M8.5 7V5.5A1.5 1.5 0 0 1 10 4h4a1.5 1.5 0 0 1 1.5 1.5V7M2.5 12.5h19"/>',
  search: '<circle cx="11" cy="11" r="6.5"/><path d="m16 16 4.5 4.5"/>',
  cal: '<rect x="3" y="5" width="18" height="16" rx="2.5"/><path d="M3 10h18M8 3v4M16 3v4"/>',
  chat: '<path d="M20.5 12.5a7.5 7.5 0 0 1-10.9 6.7L4 20.5l1.4-5A7.5 7.5 0 1 1 20.5 12.5Z"/>',
  bell: '<path d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6M10.5 20a2 2 0 0 0 3 0"/>',
  star: '<path d="m12 3.5 2.7 5.6 6.1.9-4.4 4.3 1 6.2-5.4-2.9-5.4 2.9 1-6.2L3.2 10l6.1-.9Z"/>',
  users: '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16.5 5.2a3.5 3.5 0 0 1 0 6.6M18 14.4A6.5 6.5 0 0 1 21.5 20"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  mic: '<rect x="9" y="2.5" width="6" height="11" rx="3"/><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3.5"/>',
  pin: '<path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z"/><circle cx="12" cy="10" r="2.5"/>',
  clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7v5.3l3.2 2"/>',
  alert: '<path d="M12 3.8 21 19H3Z"/><path d="M12 9.5v4.2M12 16.6v.1"/>',
  check: '<path d="m5 12.5 4.5 4.5L19 7"/>',
  euro: '<path d="M17.5 6.5a6.5 6.5 0 1 0 0 11M4 10h9M4 14h9"/>',
  set: '<circle cx="12" cy="12" r="3"/><path d="M12 2.5v2.2M12 19.3v2.2M21.5 12h-2.2M4.7 12H2.5M18.7 5.3l-1.6 1.6M6.9 17.1l-1.6 1.6M18.7 18.7l-1.6-1.6M6.9 6.9 5.3 5.3"/>',
  //: El circunflejo invertido de un desplegable. Faltaba, y `ICONS[name] ?? ""`
  //: lo pintaba como un `<svg>` vacío: el selector de producto se quedó sin su
  //: flechita y nadie se enteró hasta verlo en pantalla.
  down: '<path d="m6 9 6 6 6-6"/>',
  //: El escudo de «Bloqueos». Lo destapó el tipado de arriba: llevaba desde su
  //: rebanada pintándose vacío.
  shield: '<path d="M12 3 5 6v5.5c0 4.2 3 8 7 9.5 4-1.5 7-5.3 7-9.5V6Z"/>',
} as const satisfies Record<string, string>;

/** Sólo los iconos que existen.
 *
 *  Antes era `keyof Record<string, string>`, es decir `string`: cualquier nombre
 *  compilaba y el que no estuviera en el mapa se pintaba como un cuadro vacío.
 *  Con `as const satisfies`, `name="down"` sin su dibujo es un error de
 *  compilación en vez de un hueco en la barra. */
export type IconName = keyof typeof ICONS;

/** Los nombres que existen, **en tiempo de ejecución**.
 *
 *  Sale del mapa y no de una lista escrita a mano: así el test que comprueba que
 *  ningún icono está vacío no se puede quedar corto cuando alguien añada uno. */
export const ICON_NAMES = Object.keys(ICONS) as IconName[];

export interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
}

/** Render a mockup line-icon. Stroke inherits `currentColor`. */
export function Icon({ name, size = 16, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn("inline-block shrink-0", className)}
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: ICONS[name] ?? "" }}
    />
  );
}
